import Link from 'next/link';
import { useForm } from 'react-hook-form';
import { InformationCircleIcon } from '@heroicons/react/24/solid';
import RegistrationSuccessModal from '@/components/RegistrationSuccessModal';
import { RouterInput, trpc } from '@/utils/trpc';
import { toast } from 'react-hot-toast';

type SignUpFormData = RouterInput['user']['signup'];

export default function SignUpPage() {
  const { register, handleSubmit, reset } = useForm<SignUpFormData>();

  const signUp = trpc.user.signup.useMutation({
    onSuccess() {
      reset();
    },
    onError(error) {
      toast.error(error.message, {
        id: 'registration-error',
      });
    },
  });

  async function onSubmit(data: SignUpFormData) {
    signUp.mutate(data);
  }

  return (
    <>
      <RegistrationSuccessModal open={signUp.isSuccess} />
      <div className="flex min-h-full flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <img
            className="mx-auto h-14 w-auto"
            src="/logo.png"
            alt="site logo"
          />
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white px-4 py-8 shadow sm:rounded-lg sm:px-10">
            <div className="mb-6 rounded-md bg-blue-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <InformationCircleIcon
                    className="h-5 w-5 text-blue-400"
                    aria-hidden="true"
                  />
                </div>
                <div className="ml-3 flex-1 md:flex md:justify-between">
                  <p className="text-sm text-blue-700">
                    MySchoolAi is intended for academic use only. Please sign up
                    with your school email address to gain access to the
                    platform.
                  </p>
                </div>
              </div>
            </div>
            <form
              className="space-y-6"
              action="#"
              method="POST"
              onSubmit={handleSubmit(onSubmit)}
            >
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700"
                >
                  Email address
                </label>
                <div className="mt-2">
                  <input
                    required
                    id="email"
                    type="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    className="block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm"
                    {...register('email')}
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="fullName"
                  className="block text-sm font-medium text-gray-700"
                >
                  Full name
                </label>
                <div className="mt-2">
                  <input
                    required
                    id="fullName"
                    type="text"
                    autoComplete="name"
                    placeholder="Jane Doe"
                    className="block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm"
                    {...register('name')}
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="school"
                  className="block text-sm font-medium text-gray-700"
                >
                  School / Employer
                </label>
                <div className="mt-2">
                  <input
                    required
                    id="school"
                    type="text"
                    placeholder="School name"
                    className="block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm"
                    {...register('school')}
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="invite-code"
                  className="block text-sm font-medium text-gray-700"
                >
                  Invite Code
                </label>
                <div className="mt-2">
                  <input
                    required
                    id="invite-code"
                    type="text"
                    placeholder="Your personal invite code"
                    className="block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm"
                    {...register('inviteCode')}
                  />
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={signUp.isLoading}
                  className="flex w-full justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {signUp.isLoading ? 'Loading...' : 'Sign up'}
                </button>
              </div>
            </form>

            <div className="mt-6 flex flex-col items-center justify-center gap-4 text-sm">
              <Link
                href="/login"
                className="font-medium text-blue-600 hover:text-blue-500"
              >
                Already have an account? Sign in
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
